/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { Drawer, Typography, useMediaQuery } from '@mui/material';

import { AuthContext } from 'authentication/AuthContext';

// Components
import { ActionBar } from 'common';
import FilterBar from './filter-bar/FilterBar';
import SearchResultCard from './card/Card';

// Styles
import {
  MobileFilterDrawer,
  NoResultsDiv,
  SearchPtoSection,
  SearchResults,
  SearchResultsWrapper,
  TooManyResultsDiv,
} from './Search.styled';

// utils
import TopBar from './top-bar/TopBar.connector';

const SearchPtos = props => {
  const {
    currentAffiliate,
    fetchLocations,
    fetchOriginators,
    fetchProducts,
    fetchPtoNumbers,
    fetchRecipients,
    fetchReferenceIds,
    fetchSearchPTOs,
    locationOptions,
    originatorOptions,
    pending,
    productOptions,
    ptoList,
    ptoNumberOptions,
    recipientOptions,
    resetCreatePto,
    referenceIdOptions,
    resetSearchPtoFilters,
    setCurrentAffiliate,
    searchPtoFilters,
    updateSearchPtoFilter,
  } = props;

  const [mobileFilterDrawerOpen, setMobileFilterDrawerOpen] = useState(false);

  const { affiliates: userAffiliates, roles } = useContext(AuthContext);
  const [filteredPtoList, setFilteredPtoList] = useState([]);

  useEffect(() => {
    if (userAffiliates.length) {
      fetchSearchPTOs();
      fetchOriginators();
      fetchRecipients();
      fetchLocations();
      fetchPtoNumbers();
      fetchReferenceIds();
      fetchProducts();
    }
  }, [
    fetchLocations,
    fetchOriginators,
    fetchProducts,
    fetchPtoNumbers,
    fetchRecipients,
    fetchReferenceIds,
    fetchSearchPTOs,
    userAffiliates.length,
    currentAffiliate,
  ]);

  useEffect(() => {
    setFilteredPtoList(ptoList);
  }, [setFilteredPtoList, ptoList]);

  const handleResetFilters = () => {
    resetSearchPtoFilters(currentAffiliate);
  };

  const toggleFilterDrawer = open => setMobileFilterDrawerOpen(open);

  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });

  const filteredPTOList = filteredPtoList.filter(({ status }) => {
    // if affiliate is both originator and recipient then data only shows in "Needs Recipient Approval" or "All" filter status'
    if (
      searchPtoFilters.status !== 'all' &&
      searchPtoFilters.status !== 'allOpen' &&
      searchPtoFilters.status !== 'allClosed'
    ) {
      return status.toLowerCase() === searchPtoFilters.status.toLowerCase();
    }
    return true;
  });

  return (
    <>
      <SearchPtoSection>
        {lgUp ? (
          <>
            <TopBar roles={roles} toggleDrawer={() => toggleFilterDrawer(true)} />
            <ActionBar backLink="../open" showContinueButton={false} />
            <FilterBar
              currentAffiliate={currentAffiliate}
              locationOptions={locationOptions}
              originatorOptions={originatorOptions}
              productOptions={productOptions}
              ptoNumberOptions={ptoNumberOptions}
              recipientOptions={recipientOptions}
              referenceIdOptions={referenceIdOptions}
              resetSearchFilters={handleResetFilters}
              searchFilters={searchPtoFilters}
              setCurrentAffiliate={setCurrentAffiliate}
              updateSearchFilter={updateSearchPtoFilter}
            />
          </>
        ) : (
          <>
            <Drawer
              anchor="right"
              onClose={() => toggleFilterDrawer(false)}
              open={mobileFilterDrawerOpen}
              slotProps={{
                paper: { component: MobileFilterDrawer },
              }}
            >
              <FilterBar
                currentAffiliate={currentAffiliate}
                locationOptions={locationOptions}
                originatorOptions={originatorOptions}
                productOptions={productOptions}
                ptoNumberOptions={ptoNumberOptions}
                recipientOptions={recipientOptions}
                referenceIdOptions={referenceIdOptions}
                resetSearchFilters={handleResetFilters}
                searchFilters={searchPtoFilters}
                setCurrentAffiliate={setCurrentAffiliate}
                toggleDrawer={() => toggleFilterDrawer(false)}
                updateSearchFilter={updateSearchPtoFilter}
              />
            </Drawer>
          </>
        )}
        <SearchResultsWrapper>
          {lgUp ? null : (
            <>
              <TopBar roles={roles} toggleDrawer={() => toggleFilterDrawer(true)} />
              <ActionBar backLink="../open" showContinueButton={false} />
            </>
          )}
          <>
            {filteredPTOList.length ? (
              <SearchResults>
                {filteredPTOList.map(pto => (
                  <SearchResultCard
                    data={pto}
                    key={pto.ptoNumber}
                    resetCreatePto={resetCreatePto}
                    setCurrentPtoDialogueOpen={props.setCurrentPtoDialogueOpen}
                  />
                ))}
                {filteredPTOList.length === 50 && (
                  <TooManyResultsDiv>
                    <Typography variant="body1">50 or more search results returned.</Typography>
                    <Typography variant="body1">Please narrow search criteria.</Typography>
                  </TooManyResultsDiv>
                )}
              </SearchResults>
            ) : (
              !pending &&
              !filteredPTOList.length && (
                <NoResultsDiv>
                  <Typography variant="body1">No search results found.</Typography>
                  <Typography variant="body1">Please update search criteria.</Typography>
                </NoResultsDiv>
              )
            )}
          </>
        </SearchResultsWrapper>
      </SearchPtoSection>
    </>
  );
};

SearchPtos.propTypes = {
  currentAffiliate: shape({}),
  fetchLocations: func.isRequired,
  fetchOriginators: func.isRequired,
  fetchProducts: func.isRequired,
  fetchPtoNumbers: func.isRequired,
  fetchRecipients: func.isRequired,
  fetchReferenceIds: func.isRequired,
  fetchSearchPTOs: func.isRequired,
  locationOptions: arrayOf(shape({})).isRequired,
  originatorOptions: arrayOf(shape({})).isRequired,
  pending: bool.isRequired,
  productOptions: arrayOf(shape({})).isRequired,
  ptoList: arrayOf(shape({})).isRequired,
  ptoNumberOptions: arrayOf(shape({})).isRequired,
  recipientOptions: arrayOf(shape({})).isRequired,
  referenceIdOptions: arrayOf(shape({})).isRequired,
  resetCreatePto: func.isRequired,
  resetSearchPtoFilters: func.isRequired,
  searchPtoFilters: shape({ status: string }).isRequired,
  setCurrentAffiliate: func.isRequired,
  setCurrentPtoDialogueOpen: func.isRequired,
  updateSearchPtoFilter: func.isRequired,
};

SearchPtos.defaultProps = {
  currentAffiliate: null,
};

export default SearchPtos;
